import { ModuleRendering, SlotRendering } from '@frontend/common-types';
import { SlotStatus, SlotType } from '@frontend/slot/types';

/**
 * Calculate the bounding box for all modules (including their slots)
 */
export const calculateBoundingBox = <TModule extends ModuleRendering = ModuleRendering>(modules: TModule[]) => {
    if (modules.length === 0) {
        return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    modules.forEach((module, index) => {
        // Consider module dimensions
        // Note: Y axis is inverted (origin at center, Y increases upward)
        if (module.location && module.dimension) {
            const moduleMaxX = module.location.x + module.dimension.width;
            // For Y: since Y increases upward, the "bottom" of the module is at location.y
            // and the "top" is at location.y + height
            const moduleMinY = module.location.y; // bottom in coordinate system
            const moduleMaxY = module.location.y + module.dimension.height; // top in coordinate system

            minX = Math.min(minX, module.location.x);
            minY = Math.min(minY, moduleMinY);
            maxX = Math.max(maxX, moduleMaxX);
            maxY = Math.max(maxY, moduleMaxY);
        }

        // Also consider slot positions for accurate bounding box
        module.slots?.forEach((slot) => {
            if (slot.absolute && slot.dimension) {
                const slotMaxX = slot.absolute.x + slot.dimension.width;
                const slotMinY = slot.absolute.y;
                const slotMaxY = slot.absolute.y + slot.dimension.height;

                minX = Math.min(minX, slot.absolute.x);
                minY = Math.min(minY, slotMinY);
                maxX = Math.max(maxX, slotMaxX);
                maxY = Math.max(maxY, slotMaxY);
            }
        });
    });

    return {
        minX,
        minY,
        maxX,
        maxY,
        width: maxX - minX,
        height: maxY - minY
    };
};

/**
 * Calculate scale factor to fit the spot layout in the container
 */
export const calculateScale = (
    spotWidth: number,
    spotHeight: number,
    containerWidth: number,
    containerHeight: number,
    horizontalPadding: number = 80,
    verticalPadding: number = horizontalPadding
): number => {
    if (spotWidth <= 0 || spotHeight <= 0 || containerWidth <= 0 || containerHeight <= 0) return 1;

    const availableWidth = Math.max(containerWidth - horizontalPadding, 1);
    const availableHeight = Math.max(containerHeight - verticalPadding, 1);

    const scaleX = availableWidth / spotWidth;
    const scaleY = availableHeight / spotHeight;

    return Math.min(scaleX, scaleY, 1);
};

/**
 * Get Tailwind background color class based on slot status and occupied state
 */
export const getSlotColor = <TSlot extends SlotRendering = SlotRendering>(slot: TSlot): string => {
    // Terminal / non-functional openings are not interactive drop targets
    if (slot.type === SlotType.TERMINAL) {
        return 'bg-brand-disabled';
    }
    if (slot.status === SlotStatus.NON_FUNCTIONAL) {
        return 'bg-brand-disabled';
    }

    // If slot is occupied, show muted color regardless of status
    if (slot.occupied) {
        return 'bg-brand-muted';
    }

    switch (slot.status) {
        case SlotStatus.AVAILABLE:
        case 'available':
            return 'bg-brand-primary';
        case SlotStatus.BROKEN:
            return 'bg-status-danger';
        case SlotStatus.MERGED:
        case 'merged':
            return 'bg-status-warning';
        default:
            return 'bg-brand-primary';
    }
};

/**
 * Get text color for slot status (for better contrast)
 */
export const getSlotTextColor = <TSlot extends SlotRendering = SlotRendering>(_slot: TSlot): string => {
    return 'rgb(255, 255, 255)'; // white text for all statuses
};

/**
 * Get display label for slot based on type
 */
export const getSlotLabel = <TSlot extends SlotRendering = SlotRendering>(slot: TSlot): string => {
    if (slot.type === SlotType.TERMINAL) {
        return 'terminal';
    }
    if (slot.type === SlotType.DROP_STATION && slot.status === SlotStatus.NON_FUNCTIONAL) {
        return 'drop station';
    }
    return slot.number || slot.display || '';
};
